import React from 'react';
import { Calendar, Target, Dumbbell, Activity, BookOpen, Moon } from 'lucide-react';

interface ContextualLaunchPillProps {
  type: 'plan' | 'goal' | 'workout' | 'mobility' | 'research' | 'health' | 'sleep';
  title: string;
  subtitle?: string;
  onClick?: () => void;
  metadata?: {
    from?: string;  // "from Move" or "from Insights"
    weekDates?: string;
    progress?: string;
    goalTitle?: string;
    eventDate?: string;
    status?: string;
    [key: string]: any;
  };
}

export const ContextualLaunchPill: React.FC<ContextualLaunchPillProps> = ({ 
  type, 
  title, 
  subtitle, 
  onClick,
  metadata 
}) => {
  // Get icon based on context type
  const getIcon = () => {
    const iconProps = { size: 20, strokeWidth: 2 };
    
    switch(type) {
      case 'plan':
        return <Calendar {...iconProps} />;
      case 'goal':
        return <Target {...iconProps} />;
      case 'workout':
        return <Dumbbell {...iconProps} />;
      case 'mobility':
        return <Activity {...iconProps} />;
      case 'research':
        return <BookOpen {...iconProps} />;
      case 'health':
        return <Activity {...iconProps} />;
      case 'sleep':
        return <Moon {...iconProps} />;
      default:
        return <Calendar {...iconProps} />;
    }
  };

  // Get display label for type
  const getTypeLabel = () => {
    switch(type) {
      case 'plan': return 'Training Plan';
      case 'goal': return 'Goal';
      case 'workout': return 'Workout';
      case 'mobility': return 'Mobility';
      case 'research': return 'Research';
      case 'health': return 'Health';
      case 'sleep': return 'Sleep';
      default: return 'Item';
    }
  };

  return (
    <div 
      onClick={onClick}
      style={{
        border: '2px solid #1F6C4F',
        backgroundColor: '#FFFFFF',
        borderRadius: '8px',
        padding: '16px',
        marginBottom: '12px',
        maxWidth: '600px',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
        fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
        cursor: onClick ? 'pointer' : 'default',
      }}
    >
      {/* "from Move" or "from Insights" label */}
      {metadata?.from && (
        <div style={{ 
          fontSize: '11px', 
          color: '#666', 
          marginBottom: '8px',
          fontWeight: 500,
          letterSpacing: '0.5px',
          textTransform: 'uppercase'
        }}>
          {metadata.from}
        </div>
      )}
      
      {/* Main content area */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
        {/* Icon */}
        <div style={{ 
          color: '#1F6C4F',
          flexShrink: 0,
          marginTop: '2px'
        }}>
          {getIcon()}
        </div>
        
        {/* Text content */}
        <div style={{ flex: 1, minWidth: 0 }}>
          {/* Title */}
          <div style={{ 
            fontSize: '16px', 
            fontWeight: 600, 
            color: '#1a1a1a',
            marginBottom: subtitle ? '4px' : '0',
            lineHeight: '1.4'
          }}>
            {title}
          </div>
          
          {/* Subtitle */}
          {subtitle && (
            <div style={{ 
              fontSize: '14px', 
              color: '#666',
              lineHeight: '1.5',
              marginBottom: metadata?.progress || metadata?.status ? '6px' : '0'
            }}>
              {subtitle}
            </div>
          )}
          
          {/* Metadata row */}
          {(metadata?.progress || metadata?.status || metadata?.weekDates || metadata?.eventDate) && (
            <div style={{ 
              display: 'flex', 
              flexWrap: 'wrap',
              gap: '12px',
              marginTop: '6px',
              fontSize: '12px'
            }}>
              {/* Progress */}
              {metadata?.progress && (
                <div style={{ 
                  color: '#1F6C4F',
                  fontWeight: 500,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}>
                  <span style={{ fontSize: '14px' }}>•</span>
                  {metadata.progress}
                </div>
              )}
              
              {/* Status */}
              {metadata?.status && (
                <div style={{ 
                  color: '#1F6C4F',
                  fontWeight: 500,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}>
                  <span style={{ fontSize: '14px' }}>•</span>
                  {metadata.status}
                </div>
              )}
              
              {/* Week dates */}
              {metadata?.weekDates && (
                <div style={{ 
                  color: '#666',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}>
                  <span style={{ fontSize: '14px' }}>•</span>
                  {metadata.weekDates}
                </div>
              )}
              
              {/* Event date */}
              {metadata?.eventDate && (
                <div style={{ 
                  color: '#666',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}>
                  <span style={{ fontSize: '14px' }}>•</span>
                  {metadata.eventDate}
                </div>
              )}
            </div>
          )}
          
          {/* Goal titles (for plans) */}
          {metadata?.goals && Array.isArray(metadata.goals) && metadata.goals.length > 0 && (
            <div style={{ 
              marginTop: '8px',
              display: 'flex',
              flexWrap: 'wrap',
              gap: '6px'
            }}>
              {metadata.goals.map((goalTitle: string, index: number) => (
                <span 
                  key={index}
                  style={{
                    fontSize: '11px',
                    color: '#1F6C4F',
                    backgroundColor: '#F0F7F4',
                    padding: '3px 8px',
                    borderRadius: '4px',
                    fontWeight: 500
                  }}
                >
                  Goal: {goalTitle}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
